import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { isValid } from "date-fns";
import { Box, Typography, FormControl, FormControlLabel, RadioGroup, Divider } from "@mui/material";
import CityAutocomplete from "../../../../Inputs/CityAutocomplete/CityAutocomplete";
import PrimaryDatePicker from "../../../../Inputs/PrimaryDatePicker/PrimaryDatePicker";
import PrimaryTextField from "../../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryRadio from "../../../../Buttons/PrimaryRadio/PrimaryRadio";
import PrimaryButton from "../../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../../Buttons/SecondaryButton/SecondaryButton";
import useEquipmentTypes from "../../../../../hooks/useEquipmentTypes/useEquipmentTypes";
import { getDistance } from "../../../../../utils/calcHelpers";
import { CarrierService } from "../../../../../services/CarrierService";
import { AlertContext } from "../../../../Dashboard/Dashboard";

export default function PastePreferredLanesRow({
  index,
  row,
  defaultMode,
  defaultStartDate,
  defaultExpiryDate,
  defaultDestination,
  userIdMcld,
  activeCarrier,
  setErrorLabel,
  submitAllTriggered,
  updateCompletedCount,
  updateErrorBalance,
  updateRemovedBalance
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const { typeLabels, typeModes } = useEquipmentTypes();
  const [originCityDefault, setOriginCityDefault] = useState("");
  const [originCity, setOriginCity] = useState({});
  const [destinationCityDefault, setDestinationCityDefault] = useState("");
  const [destinationCity, setDestinationCity] = useState({});
  const [startDate, setStartDate] = useState(new Date(defaultStartDate));
  const [expiryDate, setExpiryDate] = useState(new Date(defaultExpiryDate));
  const [mode, setMode] = useState(defaultMode);
  const [proposedCost, setProposedCost] = useState(null);
  const [ratePerMile, setRatePerMile] = useState(null);
  const [weekly, setWeekly] = useState("");
  const [availableSunday, setAvailableSunday] = useState("");
  const [availableMonday, setAvailableMonday] = useState("");
  const [availableTuesday, setAvailableTuesday] = useState("");
  const [availableWednesday, setAvailableWednesday] = useState("");
  const [availableThursday, setAvailableThursday] = useState("");
  const [availableFriday, setAvailableFriday] = useState("");
  const [availableSaturday, setAvailableSaturday] = useState("");
  const [disabledWeekly, setDisabledWeekly] = useState(false);
  const [disabledDaily, setDisabledDaily] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isError, setIsError] = useState(true);
  const [isRemoved, setIsRemoved] = useState(false);
  const [payload, setPayload] = useState({});

  const weeklyInputs = [
    {
      label: "Sun",
      value: availableSunday,
      onChange: setAvailableSunday
    },
    {
      label: "Mon",
      value: availableMonday,
      onChange: setAvailableMonday
    },
    {
      label: "Tue",
      value: availableTuesday,
      onChange: setAvailableTuesday
    },
    {
      label: "Wed",
      value: availableWednesday,
      onChange: setAvailableWednesday
    },
    {
      label: "Thu",
      value: availableThursday,
      onChange: setAvailableThursday
    },
    {
      label: "Fri",
      value: availableFriday,
      onChange: setAvailableFriday
    },
    {
      label: "Sat",
      value: availableSaturday,
      onChange: setAvailableSaturday
    }
  ];

  const handleSubmit = () => {
    setSubmitDisabled(true);
    setIsLoading(true);
    CarrierService.CreatePreferredLane(instance, payload).then(() => {
      setIsLoading(false);
      updateCompletedCount();
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  };

  const handleRemove = () => {
    setRemoveLoading(!removeLoading);
    setIsRemoved(!isRemoved);
  };

  const handleModeChange = (event) => {
    setMode(event.target.value);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value) {
      let date = new Date(value.getTime());
      setExpiryDate(new Date(date.setFullYear(date.getFullYear() + 1)));
    } else {
      setExpiryDate(value);
    }
  };

  useEffect(() => {
    if (isError) {
      updateErrorBalance(1);
    } else {
      updateErrorBalance(-1);
    }
  }, [isError]);

  useEffect(() => {
    if (isRemoved) {
      updateRemovedBalance(1);
    } else {
      updateRemovedBalance(-1);
    }
  }, [isRemoved]);

  useEffect(() => {
    if (submitAllTriggered && !submitDisabled && !isRemoved) {
      handleSubmit();
    }
  }, [submitAllTriggered]);

  useEffect(() => {
    const isDisabled =
      !activeCarrier ||
      !originCity ||
      !destinationCity ||
      !startDate ||
      !expiryDate ||
      !isValid(startDate) ||
      !isValid(expiryDate) ||
      (!disabledDaily && !disabledWeekly);

    setSubmitDisabled(isDisabled);
    setIsError(isDisabled);
  }, [
    activeCarrier,
    originCity,
    destinationCity,
    startDate,
    expiryDate,
    disabledDaily,
    disabledWeekly
  ]);

  useEffect(() => {
    const newPayload = {
      carrierId: activeCarrier?.id ?? null,
      originCity: originCity?.name ?? null,
      originState: originCity?.state ?? null,
      originCityId: originCity?.id ?? null,
      destinationCity: destinationCity?.name ?? null,
      destinationState: destinationCity?.state ?? null,
      destinationCityId: destinationCity?.id ?? null,
      startDate: isValid(startDate) && startDate.toISOString(),
      endDate: isValid(expiryDate) && expiryDate.toISOString(),
      weekly: weekly ? parseInt(weekly) : 0,
      postedDate: new Date().toISOString(),
      postedBy: userIdMcld,
      proposedCost: proposedCost,
      proposedRpm: ratePerMile,
      miles: null,
      availableDaily: {
        availableMonday: availableMonday ? parseInt(availableMonday) : 0,
        availableTuesday: availableTuesday ? parseInt(availableTuesday) : 0,
        availableWednesday: availableWednesday ? parseInt(availableWednesday) : 0,
        availableThursday: availableThursday ? parseInt(availableThursday) : 0,
        availableFriday: availableFriday ? parseInt(availableFriday) : 0,
        availableSaturday: availableSaturday ? parseInt(availableSaturday) : 0,
        availableSunday: availableSunday ? parseInt(availableSunday) : 0,
      },
      equipmentTypeDaily: {
        equipmentTypeMonday: mode,
      }
    };

    if (originCity?.id && destinationCity?.id) {
      newPayload.miles =
        getDistance(
          originCity.latitude,
          originCity.longitude,
          destinationCity.latitude,
          destinationCity.longitude
        );
    }

    setPayload(newPayload);
    setDisabledDaily(!!weekly);
    setDisabledWeekly(
      !!availableSunday ||
      !!availableMonday ||
      !!availableTuesday ||
      !!availableWednesday ||
      !!availableThursday ||
      !!availableFriday ||
      !!availableSaturday
    );
  }, [
    activeCarrier,
    originCity,
    destinationCity,
    mode,
    startDate,
    expiryDate,
    proposedCost,
    ratePerMile,
    weekly,
    availableSunday,
    availableMonday,
    availableTuesday,
    availableWednesday,
    availableThursday,
    availableFriday,
    availableSaturday
  ]);

  useEffect(() => {
    setRemoveLoading(false);

    if (row.cities?.length > 0 && row.states.length > 0) {
      setOriginCityDefault(row.cities?.[0].concat(", ", row.states[0]));
    } else {
      setOriginCityDefault("");
      setErrorLabel("The information could not be fully parsed");
    }

    if (row.cities?.length > 1 && row.states.length > 1) {
      setDestinationCityDefault(row.cities[1].concat(", ", row.states[1]));
    } else if (defaultDestination && Object.keys(defaultDestination).length != 0) {
      setDestinationCityDefault(defaultDestination.name.concat(", ", defaultDestination.state));
    }

    if (row.mode) {
      let keys = Object.keys(typeModes).filter(key => typeModes[key] === row.mode);
      keys.forEach(key => {
        let mode = typeLabels.find(({ id }) => id === key);
        if (mode) setMode(mode.id);
      });
    }

    if (row.date) {
      setStartDate(row.date);
      let date = new Date(row.date.getTime());
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date.getTime() < today.getTime()) {
        while (date.getTime() < today.getTime()) {
          date = new Date(date.setFullYear(date.getFullYear() + 1));
        }
      }
      setStartDate(new Date(date));
      setExpiryDate(new Date(date.setFullYear(date.getFullYear() + 1)));
    }
  }, [row]);

  return (
    <>
      <Box
        sx={{
          backgroundColor: "black_solid.10",
          padding: "10px",
          margin: "20px 0",
          display: "flex",
          justifyContent: "space-between"
        }}>
        <Box sx={{ display: "flex" }}>
          <Typography
            sx={{
              color: isRemoved ? "black_solid.50" : "primary.60",
              marginRight: "10px"
            }}>
            PASTED ENTRY {index + 1}
          </Typography>
          {isRemoved && <Typography sx={{ color: "error.60" }}>REMOVED</Typography>}
        </Box>
        {isRemoved && !isLoading &&
          <Typography
            onClick={handleRemove}
            sx={{
              color: "primary.60",
              textDecoration: "underline",
              cursor: "pointer"
            }}>
            UNDO
          </Typography>
        }
      </Box>
      {!isRemoved &&
        <>
          <Box className="Carrier-modal-input-row">
            <Typography variant="subhead2" color="black_solid.80">
              Locations
            </Typography>
          </Box>
          <Box className="Carrier-modal-input-row">
            <Box>
              <CityAutocomplete
                label="Origin *"
                updateSelectedCity={setOriginCity}
                defaultInput={originCityDefault}
                error={!originCity}
                disableCounterIncrement
                disableAutoValue
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <CityAutocomplete
                label="Destination *"
                updateSelectedCity={setDestinationCity}
                defaultInput={destinationCityDefault}
                error={!destinationCity}
                disableCounterIncrement
                disableAutoValue
              />
            </Box>
          </Box>
          <Box className="Carrier-modal-input-row" sx={{ alignItems: "center" }}>
            <Typography variant="subhead2" color="black_solid.80">
              Mode
            </Typography>
            <FormControl sx={{ margin: "2px 0 0 10px" }}>
              <RadioGroup
                row
                aria-labelledby="carrier-search-radio-buttons"
                name="row-radio-buttons-group"
                value={mode}
                onChange={handleModeChange}
              >
                {typeLabels.map(type => (
                  <FormControlLabel
                    key={`equipment-type-${type.id}`}
                    value={type.id}
                    control={<PrimaryRadio />}
                    label={<Typography variant="body2" color="black_solid.60">{type.label}</Typography>}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
          <Divider sx={{ marginBottom: "16px" }} />
          <Box className="Carrier-modal-input-row">
            <Typography variant="subhead2" color="black_solid.80">
              Dates
            </Typography>
          </Box>
          <Box className="Carrier-modal-input-row">
            <Box>
              <PrimaryDatePicker
                label="Start Date *"
                value={startDate}
                handleChange={handleStartDateChange}
                width="260px"
                disablePast
                error={!startDate || !isValid(startDate)}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <PrimaryDatePicker
                label="Expiry Date *"
                value={expiryDate}
                handleChange={setExpiryDate}
                width="260px"
                disablePast
                error={!expiryDate || !isValid(expiryDate)}
              />
            </Box>
          </Box>
          <Divider sx={{ marginBottom: "16px" }} />
          <Box className="Carrier-modal-input-row">
            <Typography variant="subhead2" color="black_solid.80">
              Costs
            </Typography>
          </Box>
          <Box className="Carrier-modal-input-row">
            <Box>
              <PrimaryTextField
                label="Proposed Cost"
                onChange={setProposedCost}
                width="120px"
                value={proposedCost}
                isNumber
                isCurrency
                maxLength="8"
                disabled={!!ratePerMile}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <PrimaryTextField
                label="Rate Per Mile"
                onChange={setRatePerMile}
                width="120px"
                value={ratePerMile}
                isNumber
                isCurrency
                maxLength="8"
                disabled={!!proposedCost}
              />
            </Box>
          </Box>
          <Divider sx={{ marginBottom: "16px" }} />
          <Box className="Carrier-modal-input-row">
            <Box>
              <Typography variant="subhead2" color="black_solid.80">
                Weekly
              </Typography>
              <Typography variant="body2" color="black_solid.60">
                Number of times per week
              </Typography>
              <Box sx={{ marginTop: "10px" }}>
                <PrimaryTextField
                  label="Weekly"
                  onChange={setWeekly}
                  width="80px"
                  value={weekly}
                  isInteger
                  maxLength="5"
                  disabled={disabledWeekly}
                />
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ margin: "0 20px" }} />
            <Box>
              <Typography variant="subhead2" color="black_solid.80">
                Daily
              </Typography>
              <Typography variant="body2" color="black_solid.60">
                Number of times per day
              </Typography>
              <Box sx={{ marginTop: "10px", display: "flex" }}>
                {weeklyInputs.map(input => (
                  <Box key={`input-${input.label}`} sx={{ display: "block", marginRight: "16px" }}>
                    <PrimaryTextField
                      id={input.label}
                      label={input.label}
                      onChange={input.onChange}
                      width="54px"
                      value={input.value}
                      isInteger
                      maxLength="5"
                      disabled={disabledDaily}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ marginLeft: "auto", marginRight: "20px" }}>
              <SecondaryButton
                label="Remove"
                onClick={handleRemove}
                disabled={isLoading || removeLoading}
                isLoading={removeLoading}
              />
            </Box>
            <PrimaryButton
              label="Post"
              width="50px"
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={submitDisabled}
            />
          </Box>
        </>
      }
    </>
  );
}

PastePreferredLanesRow.propTypes = {
  index: PropTypes.number.isRequired,
  row: PropTypes.object.isRequired,
  defaultMode: PropTypes.string,
  defaultStartDate: PropTypes.instanceOf(Date),
  defaultExpiryDate: PropTypes.instanceOf(Date),
  defaultDestination: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  userIdMcld: PropTypes.string,
  activeCarrier: PropTypes.object.isRequired,
  setErrorLabel: PropTypes.func.isRequired,
  submitAllTriggered: PropTypes.bool.isRequired,
  updateCompletedCount: PropTypes.func.isRequired,
  updateErrorBalance: PropTypes.func.isRequired,
  updateRemovedBalance: PropTypes.func.isRequired
};